import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const SITE = "Sustainably Curated";
const DEFAULT_DESC =
  "Sustainably Curated reviews clothing brands and materials so you can quickly discover fashion made from approved sustainable, organic, and biodegradable materials.";
const FALLBACK_OG =
  "https://res.cloudinary.com/dhwggorer/image/upload/v1780763417/sustainably_curated/hero/g2quqve5jm2oskzr5tlg.png";

/**
 * Per-page SEO meta tags.
 * Pass `title`, `description`, optional `image`, and `path` (used to build canonical/og:url).
 *
 * For static pages, if `path` is provided we look up an admin-managed override
 * from /api/public/page-seo?path=… — the admin's title/description/og_image/noindex wins
 * over any defaults passed by the component.
 */
export function SeoHead({ title, description, image, path, noindex = false }) {
  const { data: override } = useQuery({
    queryKey: ["page-seo", path],
    queryFn: async () => (await api.get("/public/page-seo", { params: { path } })).data,
    enabled: !!path,
    staleTime: 60_000,
  });
  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/public/settings")).data,
    staleTime: 5 * 60_000,
  });

  const effectiveTitle = (override?.title) || title;
  const effectiveDesc = (override?.description) || description;
  const effectiveNoindex = noindex || Boolean(override?.noindex);

  const fullTitle = effectiveTitle
    ? effectiveTitle.includes(SITE) ? effectiveTitle : `${effectiveTitle} — ${SITE}`
    : SITE;
  const desc = effectiveDesc || DEFAULT_DESC;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = origin + (path || (typeof window !== "undefined" ? window.location.pathname : ""));
  const ogImage = (override?.og_image_url) || image || (settings?.default_og_image_url) || (settings?.hero_image_url) || FALLBACK_OG;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {effectiveNoindex
        ? <meta name="robots" content="noindex,nofollow" />
        : <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />}
      <link rel="canonical" href={url} />
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
